import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { cn } from "@/components/podkit/lib/cn";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { List, type ListImperativeAPI } from "react-window";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TreeNode<T = any> = {
    id: string;
    label: string;
    data?: T;
    children?: TreeNode<T>[];
    isExpanded?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FlatTreeNode<T = any> = {
    level: number;
    hasChildren: boolean;
    parentId?: string;
} & TreeNode<T>;

export type TreeListRef = {
    scrollToItem: (
        nodeId: string,
        align?: "auto" | "smart" | "center" | "end" | "start",
        behavior?: "auto" | "instant" | "smooth",
    ) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TreeListProps<T = any> = {
    nodes: TreeNode<T>[];
    height: number;
    width?: number;
    itemHeight?: number | ((index: number, cellProps: TreeItemProps<T>) => number);
    useDynamicHeight?: boolean;
    overscanCount?: number;
    className?: string;
    onNodeClick?: (node: TreeNode<T>) => void;
    onToggleExpand?: (nodeId: string, expanded: boolean) => void;
    renderNode?: (node: FlatTreeNode<T>, updateDynamicHeight: (height: number) => void) => React.ReactNode;
    onNodesRendered?: (event: {
        scrollTop: number;
        scrollLeft: number;
        scrollHeight: number;
        scrollWidth: number;
        width: number;
        height: number;
        startIndex: number;
        stopIndex: number;
        nodes: FlatTreeNode<T>[];
    }) => void;
    initialExpandedNodeIds?: Set<string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TreeItemProps<T = any> = {
    flatNodes: FlatTreeNode<T>[];
    expandedNodes: Set<string>;
    onToggleExpand: (nodeId: string) => void;
    onNodeClick?: (node: TreeNode<T>) => void;
    renderNode?: (node: FlatTreeNode<T>, updateDynamicHeight: (height: number) => void) => React.ReactNode;
    width?: number;
    updateDynamicHeight: (index: number, height: number) => void;
    _forcerRerender?: number; // Used to force re-render
};

const TreeItem = <T,>({
    index,
    style,
    flatNodes,
    expandedNodes,
    onToggleExpand,
    onNodeClick,
    renderNode,
    width,
    updateDynamicHeight,
}: TreeItemProps<T> & { index: number; style: React.CSSProperties }) => {
    const node = flatNodes[index];

    const isExpanded = expandedNodes.has(node.id);
    const paddingLeft = node.level * 8; // 16px per level

    const handleNodeClick = useCallback(() => {
        if (node.hasChildren) {
            onToggleExpand(node.id);
        }
        onNodeClick?.(node);
    }, [node, onNodeClick, onToggleExpand]);

    if (!node) return null;

    style.width = width ? `${width}px` : "auto";

    return (
        <div
            style={style}
            className="flex cursor-pointer items-center overflow-hidden px-4 hover:bg-surface-accent-always-dark"
            onClick={handleNodeClick}
            data-tracking-id-none
        >
            <div className="flex items-center" style={{ paddingLeft }}>
                {node.hasChildren ? (
                    <div
                        className="mr-1 flex h-4 w-4 items-center justify-center rounded text-always-light"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                    >
                        {isExpanded ? <IconChevronDown size="sm" /> : <IconChevronRight size="sm" />}
                    </div>
                ) : (
                    <div className="mr-1 h-4 w-4" />
                )}
            </div>
            <div className={cn("flex grow", { "overflow-hidden": !!width })}>
                {renderNode ? (
                    renderNode(node, (height) => updateDynamicHeight(index, height))
                ) : (
                    <span className="text-sm">{node.label}</span>
                )}
            </div>
        </div>
    );
};

type TreeListComponent = <T>(props: TreeListProps<T> & { ref?: React.Ref<TreeListRef> }) => React.ReactElement | null;

export const TreeList = forwardRef(
    <T,>(
        {
            nodes,
            height,
            width,
            itemHeight = 32,
            useDynamicHeight,
            overscanCount = 5,
            className,
            onNodeClick,
            onToggleExpand,
            renderNode,
            onNodesRendered,
            initialExpandedNodeIds = new Set(),
        }: TreeListProps<T>,
        ref: React.Ref<TreeListRef>,
    ) => {
        const [expandedNodes, setExpandedNodes] = useState<Set<string>>(initialExpandedNodeIds);
        const listRef = useRef<ListImperativeAPI>(null);
        const cacheRowHeight = useMemo(() => new Map<string, number>(), []);
        const [forceRerender, setForceRerender] = useState(0); // Used to force re-render when heights change

        useEffect(() => {
            setExpandedNodes((prev) => new Set([...prev, ...initialExpandedNodeIds]));
        }, [initialExpandedNodeIds]);

        const handleToggleExpand = useCallback(
            (nodeId: string) => {
                setExpandedNodes((prev) => {
                    const newSet = new Set(prev);
                    const wasExpanded = newSet.has(nodeId);

                    if (wasExpanded) {
                        newSet.delete(nodeId);
                    } else {
                        newSet.add(nodeId);
                    }

                    onToggleExpand?.(nodeId, !wasExpanded);
                    return newSet;
                });
            },
            [onToggleExpand],
        );

        const flatNodes = useMemo(() => {
            const flattenTree = (nodes: TreeNode<T>[], level = 0, parentId?: string): FlatTreeNode<T>[] => {
                const result: FlatTreeNode<T>[] = [];

                for (const node of nodes) {
                    const hasChildren = Boolean(node.children && node.children.length > 0);
                    const flatNode: FlatTreeNode<T> = {
                        ...node,
                        level,
                        hasChildren,
                        parentId,
                    };

                    result.push(flatNode);

                    // If node is expanded and has children, recursively add children
                    if (expandedNodes.has(node.id) && node.children) {
                        result.push(...flattenTree(node.children, level + 1, node.id));
                    }
                }

                return result;
            };

            return flattenTree(nodes);
        }, [nodes, expandedNodes]);

        const updateDynamicHeight = useCallback(
            (index: number, height: number) => {
                if (cacheRowHeight && cacheRowHeight.get(flatNodes[index].id) !== height) {
                    cacheRowHeight.set(flatNodes[index].id, height);
                    setForceRerender((v) => v + 1); // Force re-render to apply new height
                }
            },
            [flatNodes, cacheRowHeight],
        );

        const computeRowHeight = useCallback(
            (index: number, rowProps: TreeItemProps<T>) => {
                if (useDynamicHeight) {
                    const nodeId = flatNodes[index].id;
                    if (cacheRowHeight.has(nodeId)) {
                        return cacheRowHeight.get(nodeId)!;
                    }
                }
                return typeof itemHeight === "function" ? itemHeight(index, rowProps) : itemHeight;
            },
            [useDynamicHeight, itemHeight, flatNodes, cacheRowHeight],
        );

        const itemData = useMemo<TreeItemProps<T>>(
            () => ({
                flatNodes,
                expandedNodes,
                onToggleExpand: handleToggleExpand,
                onNodeClick,
                renderNode,
                width,
                updateDynamicHeight,
                _forcerRerender: forceRerender,
            }),
            [
                flatNodes,
                expandedNodes,
                handleToggleExpand,
                onNodeClick,
                renderNode,
                width,
                updateDynamicHeight,
                forceRerender,
            ],
        );

        const handleOnRowsRendered = useCallback(
            ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
                const element = listRef.current?.element;
                if (!element) {
                    return;
                }
                onNodesRendered?.({
                    scrollTop: element.scrollTop,
                    scrollLeft: element.scrollLeft,
                    scrollHeight: element.scrollHeight,
                    scrollWidth: element.scrollWidth,
                    width: element.clientWidth,
                    height: element.clientHeight,
                    startIndex,
                    stopIndex,
                    nodes: flatNodes,
                });
            },
            [onNodesRendered, flatNodes],
        );

        // Expose list API through ref
        useImperativeHandle(
            ref,
            () => ({
                scrollToItem: (
                    nodeId: string,
                    align?: "auto" | "smart" | "center" | "end" | "start",
                    behavior?: "auto" | "instant" | "smooth",
                ) => {
                    const index = flatNodes.findIndex((node) => node.id === nodeId);
                    listRef.current?.scrollToRow({ index, align, behavior });
                },
            }),
            [flatNodes],
        );

        return (
            <List
                className={className}
                listRef={listRef}
                style={{ height }}
                rowCount={flatNodes.length}
                rowHeight={computeRowHeight}
                rowComponent={TreeItem}
                rowProps={itemData}
                overscanCount={overscanCount}
                onRowsRendered={handleOnRowsRendered}
            />
        );
    },
) as TreeListComponent & React.ForwardRefExoticComponent<unknown>;
TreeList.displayName = "TreeList";
